import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, MessageSquare, Star } from "lucide-react";
import { motion } from "framer-motion";
import Seo, { breadcrumbSchema, faqSchema, localBusinessSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import RelatedInsights from "../components/RelatedInsights";
import { useArea, useProjects, useServices, useSettings, useTestimonials } from "../lib/hooks";
import { REGIONS, regionLabel } from "../lib/site";
import { track } from "../lib/api";

const isPrerenderHydration = () =>
  typeof window !== "undefined" && window.__NOOR_PRERENDERED__ === true;

const easeOut = [0.22, 1, 0.36, 1];

const heroStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.08 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeOut } },
};

function AreaHeroImage({ src, alt }) {
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const skip = isPrerenderHydration();
  // The slow Ken Burns drift starts only AFTER mount — and never during prerender
  // capture — so snapshots and first hydration render agree (no transform), which
  // keeps hydration clean (React #418) and the static HTML fully painted.
  const [drift, setDrift] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.__NOOR_PRERENDER_CAPTURE__) return undefined;
    const id = requestAnimationFrame(() => setDrift(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const drifting = drift && !reduceMotion;
  return (
    <div className="border border-white/15 p-2">
      <motion.div
        className="overflow-hidden aspect-[4/3]"
        initial={skip ? false : { clipPath: "inset(12% 0% 12% 0%)", opacity: 0 }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
        transition={{ duration: 1.2, ease: easeOut }}
      >
        <motion.img
          src={src}
          alt={alt}
          width="1600"
          height="1200"
          data-testid="area-hero-image"
          className="w-full h-full object-cover will-change-transform"
          loading="eager"
          fetchPriority="high"
          draggable={false}
          initial={false}
          animate={{ scale: drifting ? 1.06 : 1 }}
          transition={
            drifting
              ? { duration: 12, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }
              : { duration: 0.6, ease: easeOut }
          }
        />
      </motion.div>
    </div>
  );
}

const PROCESS_STEPS = [
  "Consultation and site visit — we listen first",
  "Design, feasibility and consents",
  "Detailed, itemised proposal",
  "Managed construction to final handover",
];

// A review left for the county town may be shown on that county's town pages.
const REVIEW_REGION_ALIASES = { taunton: ["somerset"], bridgwater: ["somerset"] };

export default function LocationPage() {
  const { slug } = useParams();
  const { data: area, isLoading, isError } = useArea(slug);
  const { data: settings } = useSettings();
  const { data: services } = useServices();
  const { data: testimonials } = useTestimonials();

  const regionValue = area?.region || REGIONS.find((r) => slug.includes(r.value))?.value;
  const { data: projects } = useProjects();

  const phone = (settings?.phone || "07782760279").replace(/\s/g, "");
  const town = (area?.short_name || "").toLowerCase();
  const regionName = (regionLabel(regionValue) || "").toLowerCase();

  // Only ever show a review genuinely from this area — never a different area's review.
  const reviewAliases = REVIEW_REGION_ALIASES[regionValue] || [];
  const areaReview = (testimonials || []).find((t) => {
    const loc = (t.location || "").toLowerCase();
    if (!loc) return false;
    return (town && loc.includes(town)) || (regionName && loc.includes(regionName)) || reviewAliases.some((a) => loc.includes(a));
  });

  // Projects: this area's curated regions, its own town floating first.
  const wantedRegions = area?.project_regions?.length ? area.project_regions : regionValue ? [regionValue] : [];
  const townParts = town.split(/[&,]/).map((s) => s.trim()).filter(Boolean);
  const areaProjects = [...(projects || [])]
    .filter((p) => wantedRegions.includes(p.region))
    .map((p) => ({
      p,
      local:
        (townParts.some((t) => (p.location || "").toLowerCase().includes(t))) ||
        (p.featured_areas || []).includes(slug)
          ? 0
          : 1,
    }))
    .sort((a, b) => a.local - b.local || (a.p.order ?? 999) - (b.p.order ?? 999))
    .map((x) => x.p);
  const ownRegionHasProjects = (projects || []).some((p) => p.region === regionValue);

  if (isLoading) return <div className="min-h-screen bg-graphite" />;
  if (isError || !area) {
    return (
      <div className="min-h-screen bg-graphite text-white flex items-center justify-center">
        <p data-testid="area-not-found">Page not found. <Link to="/areas-we-cover" className="text-ember underline">View areas we cover</Link>.</p>
      </div>
    );
  }

  return (
    <div data-testid="location-page">
      <Seo
        title={area.seo_title || `${area.name} | NOOR Design & Build`}
        description={area.seo_description || area.intro1}
        path={`/areas-we-cover/${slug}`}
        noindex={!!area.noindex}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            { ...localBusinessSchema(settings), areaServed: [area.short_name, regionLabel(regionValue)].filter(Boolean) },
            breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Areas We Cover", path: "/areas-we-cover" }, { name: area.name, path: `/areas-we-cover/${slug}` }]),
            ...(area.faqs?.length ? [faqSchema(area.faqs)] : []),
          ],
        }}
      />

      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <div className={`grid gap-10 ${area.image ? "lg:grid-cols-2 lg:items-center" : ""}`}>
            <motion.div
              variants={heroStagger}
              initial={isPrerenderHydration() ? false : "hidden"}
              animate="show"
            >
              <motion.nav variants={heroItem} className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40" aria-label="Breadcrumb" data-testid="area-breadcrumb">
                <Link to="/" className="hover:text-ember">Home</Link> / <Link to="/areas-we-cover" className="hover:text-ember">Areas We Cover</Link> / <span className="text-ember">{area.name}</span>
              </motion.nav>
              <motion.p variants={heroItem} className="font-mono text-xs tracking-[0.3em] uppercase text-ember mt-8">[ {area.eyebrow} ]</motion.p>
              <motion.h1 variants={heroItem} className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4 max-w-3xl leading-[1.05]">
                {area.name}
              </motion.h1>
              <motion.p variants={heroItem} className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">{area.intro1}</motion.p>
              <motion.div variants={heroItem} className="flex flex-col sm:flex-row flex-wrap gap-3 mt-10" data-testid="area-hero-cta">
                <Link
                  to="/request-a-consultation"
                  data-testid="area-hero-consultation-button"
                  className="btn-sheen group inline-flex items-center justify-center gap-2.5 bg-ember hover:bg-emberdark text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300"
                >
                  Request a Consultation <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                  href={`tel:${phone}`}
                  onClick={() => track("phone_click", { location: "area_hero" })}
                  data-testid="area-hero-call-button"
                  className="inline-flex items-center justify-center gap-2.5 border border-white/25 hover:border-white text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300"
                >
                  <Phone size={14} /> {settings?.phone_display || "07782 760 279"}
                </a>
                <a
                  href={`https://wa.me/44${phone.replace(/\D/g, "").slice(1)}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { location: "area_hero" })}
                  data-testid="area-hero-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2.5 border border-white/25 hover:border-white text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300"
                >
                  <MessageSquare size={14} /> WhatsApp
                </a>
              </motion.div>
            </motion.div>
            {area.image && (
              <AreaHeroImage src={area.image} alt={area.image_alt || `The character of ${area.short_name} — an area NOOR Design & Build serves`} />
            )}
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 space-y-6 text-graphite/70 leading-relaxed text-sm md:text-base">
              <p>{area.intro2}</p>
              <Reveal>
                <div className="bg-stone border border-black/10 p-8">
                  <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight text-graphite">Planning &amp; property considerations in {area.short_name}</h2>
                  <p className="text-graphite/70 leading-relaxed mt-4">{area.planning_note}</p>
                </div>
              </Reveal>
              {area.related_links?.length > 0 && (
                <Reveal>
                  <div className="flex flex-wrap gap-x-8 gap-y-3 pt-2" data-testid="area-related-links">
                    {area.related_links.map((l) => (
                      <Link key={l.to} to={l.to} className="group inline-flex items-center gap-2 text-sm font-semibold text-graphite hover:text-ember transition-colors duration-300">
                        {l.label} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    ))}
                  </div>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-5">
              <Reveal>
                <div className="border border-black/10 p-8">
                  <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight">Services in {area.short_name}</h2>
                  <p className="text-graphite/70 text-sm leading-relaxed mt-4">{area.services_note}</p>
                  <ul className="mt-6 space-y-2.5">
                    {(services || []).map((s) => (
                      <li key={s.slug}>
                        <Link to={`/services/${s.slug}`} data-testid={`area-service-${s.slug}`} className="text-sm text-graphite hover:text-ember transition-colors duration-300 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-ember inline-block shrink-0" /> {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>

          {area.sections?.length > 0 && (
            <div className="mt-16 md:mt-20 space-y-14 max-w-[920px]" data-testid="area-sections">
              {area.sections.map((s, i) => (
                <Reveal key={i}>
                  <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight" data-testid={`area-section-h2-${i}`}>{s.h2}</h2>
                  {(Array.isArray(s.text) ? s.text : [s.text]).map((p, j) => (
                    <p key={j} className="text-graphite/70 leading-relaxed mt-5 text-sm md:text-base">{p}</p>
                  ))}
                  {s.links?.length > 0 && (
                    <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
                      {s.links.map((l) => (
                        <Link key={l.to} to={l.to} className="group inline-flex items-center gap-2 text-sm font-semibold text-graphite hover:text-ember transition-colors duration-300">
                          {l.label} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      ))}
                    </div>
                  )}
                </Reveal>
              ))}
            </div>
          )}

          {areaProjects.length > 0 && (
            <div className="mt-16">
              <Reveal><h2 className="font-display font-light text-3xl md:text-4xl tracking-tight">Projects {ownRegionHasProjects ? `in ${regionLabel(regionValue) || area.short_name}` : `near ${area.short_name}`}</h2></Reveal>
              <div className="grid md:grid-cols-3 gap-6 mt-10">
                {areaProjects.slice(0, 3).map((p) => (
                  <Reveal key={p.slug}>
                    <Link to={`/projects/${p.slug}`} data-testid={`area-project-${p.slug}`} className="group block">
                      <div className="border border-black/10 p-2 transition-colors duration-500 group-hover:border-ember/60">
                        <div className="relative overflow-hidden">
                          <FrameReveal src={p.cover} alt={`${p.title} — ${p.subtitle}`} className="aspect-[4/3]" />
                          <div className="absolute inset-0 bg-graphite/0 group-hover:bg-graphite/25 max-md:bg-graphite/25 transition-colors duration-500 flex items-center justify-center">
                            <span data-testid={`area-view-project-overlay-${p.slug}`} className="font-mono text-[10px] tracking-[0.35em] uppercase text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 max-md:opacity-100 max-md:translate-y-0 transition-all duration-500 border border-white/40 px-4 py-2">
                              View Project
                            </span>
                          </div>
                        </div>
                      </div>
                      <h3 className="font-display font-medium text-lg mt-4 group-hover:text-ember transition-colors duration-300">{p.title}</h3>
                      <p className="text-sm text-graphite/55">{p.subtitle}</p>
                      {p.placeholder && <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-graphite/45 mt-2">Design concept — illustrative imagery</p>}
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {areaReview && (
            <Reveal className="mt-20">
              <figure className="border border-black/10 p-8 md:p-10 max-w-[900px]" data-testid="area-testimonial">
                <div className="flex items-center gap-1" aria-label="Five star Google review">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={14} className="text-[#D4A017]" fill="#D4A017" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="serif-accent text-graphite/80 leading-relaxed text-lg md:text-xl mt-5">“{areaReview.text}”</blockquote>
                <figcaption className="font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/40 mt-5">
                  {areaReview.name}{areaReview.location ? ` — ${areaReview.location}` : ""} · Google review
                </figcaption>
              </figure>
            </Reveal>
          )}

          <div className="mt-20 border-t border-black/10 pt-14">
            <Reveal>
              <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight">How your {area.short_name} project runs</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 mt-10">
              {PROCESS_STEPS.map((s, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="bg-cream p-6 md:p-8 h-full">
                    <p className="font-mono text-xs text-ember">[ {String(i + 1).padStart(2, "0")} ]</p>
                    <p className="text-sm text-graphite/70 leading-relaxed mt-3">{s}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-8">
              <Link to="/how-we-work" className="group inline-flex items-center gap-2 text-sm font-semibold text-graphite hover:text-ember transition-colors duration-300">
                The complete NOOR client journey <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          {area.faqs?.length > 0 && (
            <div className="max-w-[900px] mt-20">
              <Reveal>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight">Local FAQs</h2>
                <div className="mt-8" data-testid="area-faq-list">
                  {area.faqs.map((f, i) => (
                    <div key={i} data-testid={`area-faq-${i}`} className="border-b border-black/10 py-7 first:pt-0 last:border-b-0">
                      <h3 className="font-display font-medium text-base md:text-lg">{f.q}</h3>
                      <p className="text-graphite/60 leading-relaxed text-sm md:text-base mt-2.5">{f.a}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          )}

          <Reveal className="mt-16">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <Link
                to="/request-a-consultation"
                data-testid="area-consultation-button"
                className="group inline-flex items-center justify-center gap-3 bg-graphite text-white px-8 py-4 text-sm font-semibold tracking-wide hover:bg-ember transition-colors duration-300"
              >
                {area.cta_text || `Discuss your ${area.short_name} project`} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${phone}`}
                onClick={() => track("phone_click", { location: "area_page" })}
                data-testid="area-call-button"
                className="inline-flex items-center justify-center gap-3 border border-black/15 hover:border-ember px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300"
              >
                <Phone size={15} /> Call {settings?.phone_display || "07782 760 279"}
              </a>
              <a
                href={`https://wa.me/44${phone.replace(/\D/g, "").slice(1)}`}
                target="_blank" rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { location: "area_page" })}
                data-testid="area-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 border border-black/15 hover:border-ember px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300"
              >
                <MessageSquare size={15} /> WhatsApp us
              </a>
            </div>
            {area.cta_note && (
              <p className="text-sm text-graphite/55 leading-relaxed mt-5 max-w-xl" data-testid="area-cta-note">{area.cta_note}</p>
            )}
          </Reveal>
        </div>
      </section>
      <RelatedInsights title={`${area.short_name || area.name} — Useful Reading`} keywords={area.insight_keywords?.length ? area.insight_keywords : [area.short_name, ...(area.name || "").split(" ")]} />
      <CtaBand />
    </div>
  );
}
